import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useToast } from '../components/ui/toast.tsx';

export const unreadKey = ['notifications', 'unread'] as const;

/**
 * Keep the app live while signed in: one Server-Sent Events connection
 * updates the notification badge, pops a toast for new notifications and
 * refreshes any ticket lists or pages affected by a change. EventSource
 * reconnects by itself after network drops.
 */
export function useLiveUpdates(userId: number | null) {
  const client = useQueryClient();
  const toast = useToast();

  useEffect(() => {
    if (userId === null || typeof EventSource === 'undefined') return;
    const source = new EventSource('/api/notifications/stream');

    source.addEventListener('notification', (e) => {
      const n = JSON.parse((e as MessageEvent).data) as {
        title: string;
        body: string;
        url: string | null;
        unreadCount: number;
      };
      client.setQueryData(unreadKey, n.unreadCount);
      void client.invalidateQueries({ queryKey: ['notifications', 'list'] });
      toast({ tone: 'info', title: n.title, body: n.body, href: n.url ?? undefined });
    });

    source.addEventListener('notifications_read', (e) => {
      client.setQueryData(
        unreadKey,
        (JSON.parse((e as MessageEvent).data) as { unreadCount: number }).unreadCount,
      );
      void client.invalidateQueries({ queryKey: ['notifications', 'list'] });
    });

    source.addEventListener('announcements', () => {
      void client.invalidateQueries({ queryKey: ['announcements'] });
    });

    source.addEventListener('ticket', (e) => {
      const { number } = JSON.parse((e as MessageEvent).data) as { number: string };
      void client.invalidateQueries({ queryKey: ['tickets'] });
      void client.invalidateQueries({ queryKey: ['ticket', number] });
    });

    return () => source.close();
  }, [userId, client, toast]);
}
